/** The work-code strip — a horizontal, scrollable row of the section's
 *  work-code chips above the board. Tap a chip to append it to the selected
 *  lane (FS after its last bar); drag it to place anywhere; ✎ edits it.
 *  ‹ › nudge the scroll; the list also scrolls natively. */
import React from "react";
import { autoFontColor } from "../../../../utils/color";
import type { PaletteActivity } from "../helpers";
import "./ActivityPalette.css";

const ActivityPalette: React.FC<{
  palette: PaletteActivity[];
  onDragStart: (key: string) => void;
  onDragEnd: () => void;
  onOpenAdd: () => void;
  /** The "+ Add work code" picker, docked under its button when open. */
  addCodePopover?: React.ReactNode;
  /** Open the per-code editor for a chip. Omitted (no pencil) in read-only
   *  contexts. */
  onEditCode?: (code: string) => void;
  /** Disables the chip drag affordance — used in Browse mode (work codes
   * strip stays interactive for metadata, but you can't drop bars on the
   * locked board). */
  disableDrag?: boolean;
  /** Tap a chip: append it to the selected lane (FS after its last bar). */
  onTapCode?: (code: string) => void;
  /** A lane is selected — taps will land (drives the hint's emphasis). */
  tapArmed?: boolean;
  /** The single state-aware hint shown above the code chips (owned by the host
   *  so it can reflect no-train / placing / captured states). */
  hint?: React.ReactNode;
  highlightCode?: string | null;
}> = ({
  palette,
  onDragStart,
  onDragEnd,
  onOpenAdd,
  onEditCode,
  disableDrag = false,
  addCodePopover,
  onTapCode,
  tapArmed = false,
  hint,
  highlightCode,
}) => {
  const [query, setQuery] = React.useState("");
  const listRef = React.useRef<HTMLDivElement | null>(null);
  const q = query.trim().toLowerCase();
  const shown = q
    ? palette.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          (p.code ?? p.activityKey).toLowerCase().includes(q),
      )
    : palette;
  const nudge = (dir: 1 | -1) => {
    listRef.current?.scrollBy({ left: dir * 320, behavior: "smooth" });
  };
  return (
    <div className="codestrip">
      <span className="codestrip-title">Work codes</span>
      <span className="codestrip-add-wrap">
        <button
          type="button"
          className="codestrip-add"
          onClick={onOpenAdd}
          title="Add work code"
        >
          + Add work code
        </button>
        {addCodePopover}
      </span>
      <input
        type="text"
        className="codestrip-search"
        placeholder="Search codes…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      <button
        type="button"
        className="codestrip-nudge"
        aria-label="Scroll codes left"
        onClick={() => nudge(-1)}
      >
        ‹
      </button>
      <div className="codestrip-mid">
        {!disableDrag && hint && (
          <div className={"codestrip-hint" + (tapArmed ? " codestrip-hint--armed" : "")}>
            {hint}
          </div>
        )}
        <div className="codestrip-list" ref={listRef}>
        {shown.map((p) => (
          <div
            key={p.activityKey}
            className={
              "codestrip-chip" +
              (disableDrag ? " codestrip-chip--no-drag" : "") +
              (highlightCode === p.activityKey ? " codestrip-chip--hl" : "")
            }
            style={{ borderColor: p.color }}
            draggable={!disableDrag}
            onClick={() => onTapCode?.(p.activityKey)}
            onDragStart={(e) => {
              if (disableDrag) {
                e.preventDefault();
                return;
              }
              e.dataTransfer.setData("text/plain", p.activityKey);
              e.dataTransfer.effectAllowed = "copy";
              onDragStart(p.activityKey);
            }}
            onDragEnd={onDragEnd}
            title={
              tapArmed
                ? `Tap: add ${p.name} to the selected lane (FS) · drag to place free`
                : `Select a lane to tap-add · drag to place free`
            }
          >
            <b
              className="codestrip-chip-code"
              style={{ background: p.color, color: autoFontColor(p.color) }}
            >
              {p.code ?? p.activityKey}
            </b>
            <span className="codestrip-chip-name">{p.name}</span>
            {p.default_duration != null && (
              <span className="codestrip-chip-dur">{p.default_duration}d</span>
            )}
            {onEditCode && (
              <button
                type="button"
                className="codestrip-chip-edit"
                title={`Edit ${p.name}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onEditCode(p.activityKey);
                }}
                onMouseDown={(e) => e.stopPropagation()}
                draggable={false}
              >
                ✎
              </button>
            )}
          </div>
        ))}
        {shown.length === 0 && (
          <span className="codestrip-nomatch">No matching codes</span>
        )}
        </div>
      </div>
      <button
        type="button"
        className="codestrip-nudge"
        aria-label="Scroll codes right"
        onClick={() => nudge(1)}
      >
        ›
      </button>
    </div>
  );
};

export default ActivityPalette;
